//@ts-nocheck
import { ChartOptions } from "chart.js";
import currency from "currency.js";
import { useCallback, useContext, useMemo, useState, WheelEvent } from "react";
import { useTranslation } from "react-i18next";
import { SettingsContext } from "../../../contexts/SettingsContext";
import {
  ChartContainer,
  Container,
  ContentContainer,
  HeaderContainer,
  LabelValue,
  NoDataContainer,
  ZoomControls,
} from "../style";
import { ReactComponent as PlusIcon } from "../../../assets/svg/plus.svg";
import { ReactComponent as MinusIcon } from "../../../assets/svg/minus.svg";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";
import useScreenSize from "../../../hooks/screenSize/useScreenSize";
import { IDepthChartProps } from "./types";
import {
  upColor,
  downColor,
  MAX_ZOOM,
  computeSortedBidAsks,
  computeLabelsAndDatas,
  computeScaleRanges,
  getChartOptions,
} from "./helpers";
import DepthChartCanvas from "./DepthChartCanvas";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";

function DepthChart({ bids, asks, asset, instrumentType }: IDepthChartProps) {
  const [zoom, setZoom] = useState(0);
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  // Subscribe so the chart re-renders when the direction scheme changes.
  useContext(SettingsContext);
  const { isMobileScreen } = useScreenSize();
  const { t } = useTranslation("app", { keyPrefix: "DepthChart" });

  const precision = useCallback(
    () => getMarketPrecision(asset, instrumentType),
    [asset, getMarketPrecision, instrumentType]
  );

  const [hoveredIndex, setHoveredIndex] = useState<
    | {
        bidIndex?: number;
        askIndex?: number;
      }
    | undefined
  >(undefined);

  const sortedBidAsks = useMemo(
    () => computeSortedBidAsks(bids, asks, zoom),
    [asks, bids, zoom]
  );

  const labelsAndDatas = useMemo(
    () => computeLabelsAndDatas(sortedBidAsks, hoveredIndex),
    [hoveredIndex, sortedBidAsks]
  );

  const scaleRanges = useMemo(
    () => computeScaleRanges(sortedBidAsks),
    [sortedBidAsks]
  );

  const options = useMemo(
    (): ChartOptions<"line"> =>
      getChartOptions(scaleRanges, precision, sortedBidAsks, setHoveredIndex),
    [sortedBidAsks, scaleRanges, precision]
  );

  const getData = {
    labels: labelsAndDatas.labels,
    datasets: [
      {
        label: "bids",
        data: labelsAndDatas.bidsData,
        type: "line",
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 1,
        borderColor: upColor(),
        backgroundColor: upColor(),
        stepped: "after",
      },
      {
        label: "asks",
        data: labelsAndDatas.asksData,
        type: "line",
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 1,
        borderColor: downColor(),
        backgroundColor: downColor(),
        stepped: "before",
      },
      {
        label: "hovered",
        data: labelsAndDatas.hoveredData,
        type: "line",
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        label: "hoveredTotalCostsData",
        data: labelsAndDatas.hoveredTotalCostsData,
        type: "line",
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const onToggleZoom = useCallback((zoomIn: boolean) => {
    setZoom((z) => {
      const newZoom = zoomIn ? z + 0.025 : z - 0.025;
      if (newZoom < 0) {
        return 0;
      }
      if (newZoom > MAX_ZOOM) {
        return MAX_ZOOM;
      }
      return newZoom;
    });
  }, []);

  const onWheel = useCallback(
    (e: WheelEvent<HTMLDivElement>) => {
      const { deltaY } = e;
      if (deltaY > 0) {
        onToggleZoom(true);
      } else if (deltaY < 0) {
        onToggleZoom(false);
      }
    },
    [onToggleZoom]
  );

  return (
    <Container>
      <HeaderContainer>
        <LabelValue>
          <span>{t("mid_market_price")}</span>
          <span>
            {sortedBidAsks.midPrice === undefined
              ? UNKNOWN_DISPLAY
              : currency(sortedBidAsks.midPrice).format()}
          </span>
        </LabelValue>
        <ZoomControls>
          <button type="button" onClick={() => onToggleZoom(false)}>
            <MinusIcon />
          </button>
          <span>{(zoom * 100).toFixed(2)}%</span>
          <button type="button" onClick={() => onToggleZoom(true)}>
            <PlusIcon />
          </button>
        </ZoomControls>
      </HeaderContainer>
      <ContentContainer>
        {!sortedBidAsks.sortedAsks.length &&
        !sortedBidAsks.sortedBids.length ? (
          <NoDataContainer>{t("no_data")}</NoDataContainer>
        ) : (
          <ChartContainer onWheel={onWheel} isMobileScreen={isMobileScreen}>
            <DepthChartCanvas
              data={getData}
              options={options}
              asset={asset}
              precision={precision}
            />
          </ChartContainer>
        )}
      </ContentContainer>
    </Container>
  );
}

export default DepthChart;
